import { useState } from 'react';
import { AlertTriangle, ArrowRight, Check, Gauge, X } from 'lucide-react';
import { useWorkspace, authHeaders } from '../../context/WorkspaceContext';
import { Alert, Badge, Button, EmptyState, FormField, PageHeader, SectionHeader, Skeleton } from '../../components/ui';
import { useToast } from '../../components/ui/ToastProvider';
import DataTable from '../../components/ui/DataTable';
import { scanFindings, failedFindings } from './insights';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const FIX_ROUTES = {
  title: { label: 'Write a local page', to: '/dashboard/ai-studio/seo_page_writer' },
  description: { label: 'Write a local page', to: '/dashboard/ai-studio/seo_page_writer' },
  heading: { label: 'Write a local page', to: '/dashboard/ai-studio/seo_page_writer' },
  phone: { label: 'Update business details', to: '/dashboard/setup' },
  address: { label: 'Update business details', to: '/dashboard/setup' },
  https: { label: 'Update business details', to: '/dashboard/setup' },
  mobile: { label: 'Rebuild your site', to: '/dashboard/ai-studio/website_builder' },
};

export default function VisibilityPage() {
  const { workspace, loading, reload } = useWorkspace();
  const { notify } = useToast();
  const saved = workspace.optimization;
  const [form, setForm] = useState({ business: '', website: '', location: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const payload = { ...form, business: form.business || saved?.business || '', website: form.website || saved?.website || '', location: form.location || saved?.location || '' };
      const response = await fetch(`${API}/tools/visibility-scan`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Scan failed.');
      await reload();
      notify('Visibility scan complete.');
    } catch (requestError) {
      setError(requestError.message);
      notify(requestError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={200} /><Skeleton height={260} /></div>;

  const scans = workspace.scans || [];
  const latest = scans[0];
  const findings = scanFindings(latest);
  const issues = failedFindings(latest);

  return (
    <div className="dt-stack-5">
      <PageHeader
        title="Visibility"
        description="See what local customers and search engines find on your website."
        actions={<Badge tone="success">Free on every plan</Badge>}
      />

      {latest ? (
        <section className="dt-card" aria-label="Visibility score">
          <div className="visibility-summary">
            <div className="score-dial" style={{ '--score': `${Number(latest.score) * 3.6}deg` }} role="img" aria-label={`Visibility score ${latest.score} out of 100`}>
              <span><strong>{latest.score}</strong><small>out of 100</small></span>
            </div>
            <div className="dt-stack-3">
              <SectionHeader title={latest.business || 'Latest scan'} description={`${latest.website || 'Your website'} · ${latest.location || 'Local market'} · scanned ${new Date(latest.created_at).toLocaleDateString()}`} />
              <div className="signal-grid">
                {findings.map(item => (
                  <div className={`signal-item ${item.passed ? 'is-pass' : 'is-fail'}`} key={item.key}>
                    <span className="signal-dot">{item.passed ? <Check size={12} /> : <X size={12} />}</span>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="dt-card">
          <EmptyState
            icon={Gauge}
            title="No scan yet"
            description="Run a free scan to see how your website performs against seven foundational local-search signals."
          />
        </div>
      )}

      {issues.length > 0 && (
        <section className="dt-stack-3" aria-label="Visibility issues">
          <SectionHeader title="What is hurting your visibility?" description={`${issues.length} ${issues.length === 1 ? 'signal is' : 'signals are'} missing from your website.`} />
          <ul className="issue-list">
            {issues.map(issue => {
              const fix = FIX_ROUTES[issue.key] || { label: 'Open AI Studio', to: '/dashboard/ai-studio' };
              return (
                <li className="issue-item" key={issue.key}>
                  <div>
                    <span className="action-card-title"><Badge tone="warning"><AlertTriangle size={12} /> Needs attention</Badge></span>
                    <strong className="dt-card-title">{issue.label}</strong>
                  </div>
                  <Button variant="secondary" size="sm" to={fix.to}>{fix.label}<ArrowRight size={14} /></Button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="dt-card dt-stack-4" aria-label="Run a scan">
        <SectionHeader title="Run a new scan" description="Check the live website again after you make changes." />
        <form className="dt-stack-4" onSubmit={submit}>
          <div className="dt-field-grid">
            <FormField label="Business" id="scan-business"><input id="scan-business" className="dt-input" name="business" value={form.business} onChange={change} placeholder={saved?.business || 'Acme Dental'} /></FormField>
            <FormField label="Website" id="scan-website" required><input id="scan-website" className="dt-input" name="website" value={form.website} onChange={change} placeholder={saved?.website || 'example.com'} required /></FormField>
            <FormField label="Target location" id="scan-location"><input id="scan-location" className="dt-input" name="location" value={form.location} onChange={change} placeholder={saved?.location || 'Orlando, FL'} /></FormField>
            <FormField label="Report email (optional)" id="scan-email"><input id="scan-email" className="dt-input" type="email" name="email" value={form.email} onChange={change} placeholder="you@example.com" /></FormField>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div><Button type="submit" loading={busy}>{busy ? 'Inspecting website...' : 'Run visibility scan'}</Button></div>
        </form>
      </section>

      {scans.length > 1 && (
        <section className="dt-stack-3" aria-label="Scan history">
          <SectionHeader title="Scan history" description="Track whether your score improves after each change." />
          <DataTable
            rows={scans}
            searchKeys={['business', 'website', 'location']}
            searchPlaceholder="Search scans"
            initialSort={{ id: 'date', direction: 'desc' }}
            caption="Previous visibility scans"
            columns={[
              { id: 'business', header: 'Business', primary: true, render: row => row.business || '—', sortValue: row => row.business || '' },
              { id: 'location', header: 'Market', render: row => row.location || '—' },
              { id: 'score', header: 'Score', render: row => <Badge tone={row.score >= 70 ? 'success' : row.score >= 40 ? 'warning' : 'danger'}>{row.score}/100</Badge>, sortValue: row => Number(row.score) },
              { id: 'issues', header: 'Issues', render: row => failedFindings(row).length },
              { id: 'date', header: 'Scanned', render: row => new Date(row.created_at).toLocaleDateString(), sortValue: row => new Date(row.created_at).getTime() },
            ]}
            renderCard={row => (
              <>
                <strong className="dt-card-title">{row.business || 'Scan'}</strong>
                <dl>
                  <dt>Score</dt><dd>{row.score}/100</dd>
                  <dt>Market</dt><dd>{row.location || '—'}</dd>
                  <dt>Scanned</dt><dd>{new Date(row.created_at).toLocaleDateString()}</dd>
                </dl>
              </>
            )}
          />
        </section>
      )}
    </div>
  );
}
